import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  QuotationSettingApiRecord,
  QuotationSettingResolveResponse,
  QuotationSettingSavePayload,
  QuotationSettingSettings
} from './quotation-setting.model';

@Injectable({
  providedIn: 'root'
})
export class QuotationSettingService {
  private readonly baseUrl = `${environment.apiUrlHost}/CP/quotation-setting`;

  private settingsCache: QuotationSettingSettings | null = null;
  private settingsRequest$: Observable<QuotationSettingSettings> | null = null;
  private resolveCache: boolean | null = null;
  private resolveRequest$: Observable<boolean> | null = null;

  constructor(private http: HttpClient) {
    this.isQuotationAllowed().subscribe();
  }

  getSettings(): Observable<QuotationSettingSettings> {
    if (this.settingsCache) {
      return of(this.settingsCache);
    }

    if (!this.settingsRequest$) {
      this.settingsRequest$ = this.http.get<QuotationSettingApiRecord>(`${this.baseUrl}/`).pipe(
        map((record) => this.apiRecordToSettings(record)),
        tap((settings) => {
          this.settingsCache = settings;
        }),
        catchError((error) => {
          console.error('Error loading quotation setting', error);
          return of({ enabled: false });
        }),
        shareReplay(1)
      );
    }

    return this.settingsRequest$;
  }

  saveSettings(settings: QuotationSettingSettings): Observable<QuotationSettingSettings> {
    const payload: QuotationSettingSavePayload = { enabled: settings.enabled };

    return this.http.put<QuotationSettingApiRecord>(`${this.baseUrl}/`, payload).pipe(
      map((record) => this.apiRecordToSettings(record)),
      tap((saved) => {
        this.settingsCache = saved;
        this.settingsRequest$ = of(saved);
        this.resolveCache = null;
        this.resolveRequest$ = null;
      }),
      catchError((error) => {
        console.error('Error saving quotation setting', error);
        throw error;
      })
    );
  }

  /** Whether quotation features are enabled for the portal. */
  isQuotationAllowed(): Observable<boolean> {
    if (this.resolveCache !== null) {
      return of(this.resolveCache);
    }

    if (!this.resolveRequest$) {
      this.resolveRequest$ = this.http
        .get<QuotationSettingResolveResponse>(`${this.baseUrl}/resolve/`)
        .pipe(
          map((response) => !!response.allow_quotation),
          tap((allowed) => {
            this.resolveCache = allowed;
          }),
          catchError(() => this.resolveFromSettings()),
          shareReplay(1)
        );
    }

    return this.resolveRequest$;
  }

  /** True when purchase should be disabled (missing/invalid price, or zero price while quotation is off). */
  isPurchaseDisabledByPrice(
    price: unknown,
    options?: { allowUnknownPrice?: boolean }
  ): boolean {
    const quotationAllowed = this.resolveCache ?? false;

    if (price === null || price === undefined || price === '') {
      return !options?.allowUnknownPrice;
    }

    const num = Number(price);
    if (!Number.isFinite(num) || num < 0) {
      return true;
    }

    return !quotationAllowed && num === 0;
  }

  clearCache(): void {
    this.settingsCache = null;
    this.settingsRequest$ = null;
    this.resolveCache = null;
    this.resolveRequest$ = null;
  }

  private resolveFromSettings(): Observable<boolean> {
    if (this.settingsCache) {
      this.resolveCache = this.settingsCache.enabled;
      return of(this.settingsCache.enabled);
    }

    return this.http.get<QuotationSettingApiRecord>(`${this.baseUrl}/`).pipe(
      map((record) => {
        const settings = this.apiRecordToSettings(record);
        this.settingsCache = settings;
        this.resolveCache = settings.enabled;
        return settings.enabled;
      }),
      catchError(() => {
        this.resolveCache = false;
        return of(false);
      })
    );
  }

  private apiRecordToSettings(
    record: QuotationSettingApiRecord | null | undefined
  ): QuotationSettingSettings {
    return {
      enabled: !!record?.enabled
    };
  }
}
